import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const blankApplication = () => ({
  name: 'New Application',
  url: '',
  note: '',
  sort_order: 1,
  is_active: true,
});

const formatApiError = (error, fallback) => {
  const payload = error?.response?.data;
  if (payload?.errors && typeof payload.errors === 'object') {
    const messages = Object.values(payload.errors).find((entry) => Array.isArray(entry) && entry.length > 0);
    if (messages) return messages.join(' ');
  }
  return payload?.message || fallback;
};

const ApplicationsCatalogManager = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const loadApplications = useCallback(async () => {
    setLoading(true);
    setNotice('');
    setError('');

    try {
      const { data } = await axios.get('/admin/hosted-applications');
      setApplications(Array.isArray(data.applications) ? data.applications : []);
    } catch (requestError) {
      setError(formatApiError(requestError, 'Failed to load application catalog.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const mutateApplication = (index, mutator) => {
    setApplications((previous) => {
      const next = previous.map((application) => ({ ...application }));
      mutator(next[index]);
      return next;
    });
  };

  const moveApplication = (index, direction) => {
    setApplications((previous) => {
      const target = index + direction;
      if (target < 0 || target >= previous.length) return previous;
      const next = [...previous];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next.map((application, orderIndex) => ({
        ...application,
        sort_order: orderIndex + 1,
      }));
    });
  };

  const addApplication = () => {
    setApplications((previous) => [
      ...previous,
      {
        ...blankApplication(),
        sort_order: previous.length + 1,
      },
    ]);
  };

  const removeApplication = (index) => {
    setApplications((previous) =>
      previous
        .filter((_, itemIndex) => itemIndex !== index)
        .map((application, orderIndex) => ({ ...application, sort_order: orderIndex + 1 }))
    );
  };

  const saveApplications = async () => {
    setSaving(true);
    setNotice('');
    setError('');

    try {
      const payload = applications.map((application, index) => ({
        name: application.name,
        url: application.url || '',
        note: application.note || '',
        sort_order: index + 1,
        is_active: Boolean(application.is_active),
      }));

      const { data } = await axios.put('/admin/hosted-applications', {
        applications: payload,
      });
      setApplications(Array.isArray(data.applications) ? data.applications : payload);
      setNotice(data.message || 'Application catalog updated.');
    } catch (requestError) {
      setError(formatApiError(requestError, 'Failed to save application catalog.'));
    } finally {
      setSaving(false);
    }
  };

  const resetApplications = async () => {
    setSaving(true);
    setNotice('');
    setError('');

    try {
      const { data } = await axios.post('/admin/hosted-applications/reset');
      setApplications(Array.isArray(data.applications) ? data.applications : []);
      setNotice(data.message || 'Application catalog reset.');
    } catch (requestError) {
      setError(formatApiError(requestError, 'Failed to reset application catalog.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="card">Loading application catalog...</div>;
  }

  return (
    <section className="homepage-builder">
      <div className="card">
        <div className="homepage-builder__header">
          <div>
            <h2>Application Catalog</h2>
            <p className="text-muted">
              Edit the applications shown on the public Applications page. URL values can point to hosted apps such as
              /apps/kcblc or external links.
            </p>
          </div>

          <div className="homepage-builder__actions">
            <a className="btn btn-muted" href="/applications" target="_blank" rel="noreferrer">
              View Applications
            </a>
            <button className="btn btn-muted" type="button" onClick={loadApplications}>
              Reload
            </button>
            <button className="btn btn-muted" type="button" onClick={resetApplications} disabled={saving}>
              Reset Defaults
            </button>
            <button className="btn btn-primary" type="button" onClick={saveApplications} disabled={saving}>
              {saving ? 'Saving...' : 'Save Catalog'}
            </button>
          </div>
        </div>

        {notice && <div className="notice notice-success">{notice}</div>}
        {error && <div className="notice notice-danger">{error}</div>}
      </div>

      <div className="homepage-builder__sections">
        {applications.map((application, index) => (
          <article key={`${application.id || 'new'}-${index}`} className="card homepage-section">
            <div className="homepage-builder__row">
              <h3>
                #{index + 1} {application.name || 'Untitled Application'}
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button className="btn btn-muted" type="button" onClick={() => moveApplication(index, -1)}>
                  Up
                </button>
                <button className="btn btn-muted" type="button" onClick={() => moveApplication(index, 1)}>
                  Down
                </button>
                <button className="btn btn-danger" type="button" onClick={() => removeApplication(index)}>
                  Remove
                </button>
              </div>
            </div>

            <div className="homepage-builder__grid">
              <div className="field">
                <label>Name</label>
                <input
                  className="input"
                  value={application.name || ''}
                  onChange={(event) =>
                    mutateApplication(index, (next) => {
                      next.name = event.target.value;
                    })
                  }
                />
              </div>

              <div className="field">
                <label>URL / Button Target</label>
                <input
                  className="input"
                  value={application.url || ''}
                  placeholder="/apps/kingdom-staff-record/"
                  onChange={(event) =>
                    mutateApplication(index, (next) => {
                      next.url = event.target.value;
                    })
                  }
                />
              </div>

              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Note</label>
                <textarea
                  className="input"
                  rows={2}
                  value={application.note || ''}
                  onChange={(event) =>
                    mutateApplication(index, (next) => {
                      next.note = event.target.value;
                    })
                  }
                />
              </div>

              <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.65rem' }}>
                <input
                  type="checkbox"
                  checked={Boolean(application.is_active)}
                  onChange={(event) =>
                    mutateApplication(index, (next) => {
                      next.is_active = event.target.checked;
                    })
                  }
                />
                Show on public Applications page
              </label>
            </div>
          </article>
        ))}
      </div>

      <div className="card">
        <button className="btn btn-primary" type="button" onClick={addApplication}>
          Add Application
        </button>
      </div>
    </section>
  );
};

export default ApplicationsCatalogManager;
